import { useEffect, useState } from "react";
import {
  Building2,
  CalendarClock,
  FileText,
  Globe,
  Hash,
  Globe2,
  Info as InfoIcon,
  Laptop,
  Monitor,
  Loader2,
  MessageSquareQuote,
  Route,
  ScrollText,
  Store,
  User,
  X,
} from "lucide-react";
import { toast } from "sonner";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogTitle,
} from "@/components/ui/dialog";
import { Badge } from "@/components/ui/badge";
import { getActionLogDetail } from "@/Api/actionLogs";
import {
  ActionLogDetail,
  MODULE_LABELS,
  actionBadgeClass,
  formatDateTime,
  parseUserAgent,
} from "../types";

interface Props {
  uuid: string | null;
  onClose: () => void;
}

/** Nomes legíveis dos campos guardados no "antes/depois" dos logs. */
const FIELD_LABELS: Record<string, string> = {
  name: "Nome",
  validation_date: "Data de validade",
  prize: "Prêmio",
  lottery_id: "Loteria (id)",
  contest_status_id: "Status (id)",
  product_classification_ids: "Bolões (ids de classificação)",
};

/** Nomes legíveis das chaves de "informações adicionais" (metadata). */
const METADATA_LABELS: Record<string, string> = {
  reason: "Motivo",
  partial: "Parcial",
  products: "Produtos",
  requester_id: "Solicitante (id)",
  sale_change_request_uuid: "Solicitação (uuid)",
};

const humanizeKey = (key: string) => {
  const text = key.replace(/_/g, " ");
  return text.charAt(0).toUpperCase() + text.slice(1);
};

const isMono = (value: unknown) =>
  typeof value === "string" &&
  /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(value);

const formatValue = (value: unknown) => {
  if (value === null || value === undefined || value === "") return "—";
  if (typeof value === "boolean") return value ? "Sim" : "Não";
  if (Array.isArray(value)) return value.length ? value.join(", ") : "—";
  if (typeof value === "object") return JSON.stringify(value);
  return String(value);
};

const Info = ({
  icon: Icon,
  label,
  value,
}: {
  icon: React.ElementType;
  label: string;
  value: React.ReactNode;
}) => (
  <div className="flex items-center gap-2 rounded-lg border bg-white px-2.5 py-2 dark:bg-zinc-900">
    <div className="flex h-7 w-7 shrink-0 items-center justify-center rounded-md bg-primary/10 text-primary">
      <Icon className="h-3.5 w-3.5" />
    </div>
    <div className="min-w-0">
      <p className="text-[11px] leading-tight text-muted-foreground">{label}</p>
      <p className="break-words text-[13px] font-semibold leading-snug">
        {value || "—"}
      </p>
    </div>
  </div>
);

const Section = ({
  icon: Icon,
  title,
  children,
}: {
  icon: React.ElementType;
  title: string;
  children: React.ReactNode;
}) => (
  <section>
    <h3 className="mb-2 flex items-center gap-2 text-sm font-semibold text-primary">
      <Icon className="h-4 w-4" />
      {title}
    </h3>
    {children}
  </section>
);

export const ActionLogDetailModal = ({ uuid, onClose }: Props) => {
  const [log, setLog] = useState<ActionLogDetail | null>(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!uuid) {
      setLog(null);
      return;
    }

    let active = true;

    (async () => {
      setLoading(true);
      const response = await getActionLogDetail(uuid);

      if (!active) return;

      setLoading(false);

      if (!response.ok) {
        toast.error(
          response.data?.message ?? "Não foi possível carregar o log."
        );
        onClose();
        return;
      }

      setLog(response.data?.response ?? null);
    })();

    return () => {
      active = false;
    };
  }, [uuid, onClose]);

  const metadata = (log?.metadata ?? {}) as Record<string, any>;
  const before = metadata.before as Record<string, unknown> | undefined;
  const after = metadata.after as Record<string, unknown> | undefined;
  const changedFields: string[] = Array.isArray(metadata.changed_fields)
    ? metadata.changed_fields
    : Object.keys(after ?? before ?? {});

  const extraEntries = Object.entries(metadata).filter(
    ([key]) => !["before", "after", "changed_fields"].includes(key)
  );
  const reason = extraEntries.find(([key]) => key === "reason")?.[1];
  const otherEntries = extraEntries.filter(([key]) => key !== "reason");
  const hasReason = reason !== undefined && reason !== null && reason !== "";

  return (
    <Dialog open={!!uuid} onOpenChange={(open) => !open && onClose()}>
      <DialogContent
        hideCloseButton
        className="max-h-[90vh] max-w-2xl gap-0 overflow-hidden p-0 sm:p-0"
      >
        <div className="relative bg-gradient-to-br from-primary to-primary/80 px-6 py-5 text-primary-foreground">
          <button
            type="button"
            onClick={onClose}
            aria-label="Fechar"
            className="absolute right-4 top-4 rounded-full p-1 text-primary-foreground/80 transition hover:bg-white/20 hover:text-primary-foreground focus:outline-none focus:ring-2 focus:ring-white/60"
          >
            <X className="h-4 w-4" />
          </button>

          <div className="flex items-start gap-3 pr-8">
            <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-white/20">
              <ScrollText className="h-5 w-5" />
            </div>
            <div className="min-w-0">
              <DialogTitle className="text-lg font-semibold leading-tight">
                {log?.log_action?.label ?? "Detalhes do log"}
              </DialogTitle>
              <DialogDescription className="mt-1 text-sm text-primary-foreground/85">
                {log?.description ?? " "}
              </DialogDescription>
              {log && (
                <div className="mt-3 flex flex-wrap items-center gap-2">
                  <Badge className={actionBadgeClass(log.log_action.key)}>
                    {log.log_action.label}
                  </Badge>
                  <Badge className="border-white/30 bg-white/15 text-primary-foreground hover:bg-white/15">
                    {MODULE_LABELS[log.log_action.module]}
                  </Badge>
                </div>
              )}
            </div>
          </div>
        </div>

        <div className="max-h-[calc(90vh-9rem)] overflow-y-auto bg-zinc-50 p-6 dark:bg-zinc-950">
          {loading || !log ? (
            <div className="flex justify-center py-10">
              <Loader2 className="h-6 w-6 animate-spin text-primary" />
            </div>
          ) : (
            <div className="space-y-6">
              {hasReason && (
                <Section icon={MessageSquareQuote} title="Motivo">
                  <div className="rounded-lg border-l-4 border-primary bg-primary/5 p-4 text-sm font-medium">
                    {formatValue(reason)}
                  </div>
                </Section>
              )}

              {otherEntries.length > 0 && (
                <Section icon={InfoIcon} title="Informações adicionais">
                  <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                    {otherEntries.map(([key, value]) => (
                      <div
                        key={key}
                        className="rounded-lg border border-primary/20 bg-primary/5 p-3"
                      >
                        <p className="text-xs text-muted-foreground">
                          {METADATA_LABELS[key] ?? humanizeKey(key)}
                        </p>
                        <p
                          className={`break-all text-sm font-semibold ${
                            isMono(value) ? "font-mono text-xs" : ""
                          }`}
                        >
                          {formatValue(value)}
                        </p>
                      </div>
                    ))}
                  </div>
                </Section>
              )}

              {(before || after) && changedFields.length > 0 && (
                <Section
                  icon={FileText}
                  title={
                    before && after
                      ? "Alterações"
                      : after
                      ? "Dados registrados"
                      : "Dados antes da ação"
                  }
                >
                  <div className="overflow-x-auto rounded-lg border bg-white dark:bg-zinc-900">
                    <table className="w-full text-sm">
                      <thead className="bg-primary/10 text-left text-xs text-primary">
                        <tr>
                          <th className="px-3 py-2">Campo</th>
                          {before && <th className="px-3 py-2">Antes</th>}
                          {after && <th className="px-3 py-2">Depois</th>}
                        </tr>
                      </thead>
                      <tbody>
                        {changedFields.map((field) => (
                          <tr key={field} className="border-t">
                            <td className="px-3 py-2 font-medium">
                              {FIELD_LABELS[field] ?? field}
                            </td>
                            {before && (
                              <td className="px-3 py-2 text-muted-foreground">
                                {formatValue(before[field])}
                              </td>
                            )}
                            {after && (
                              <td className="px-3 py-2 font-medium">
                                {formatValue(after[field])}
                              </td>
                            )}
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </Section>
              )}

              <Section icon={Hash} title="Detalhes do registro">
                <div className="grid grid-cols-1 gap-2 sm:grid-cols-3">
                  <Info
                    icon={CalendarClock}
                    label="Data e hora"
                    value={formatDateTime(log.created_at)}
                  />
                  <div className="sm:col-span-2">
                    <Info
                      icon={User}
                      label="Usuário"
                      value={
                        log.user
                          ? `${log.user.name} (${log.user.email})`
                          : log.user_name ?? "Usuário removido"
                      }
                    />
                  </div>
                  <Info icon={Hash} label="Registro" value={log.entity_label} />
                  <Info
                    icon={Store}
                    label="Vendedor"
                    value={
                      log.seller
                        ? log.seller.user?.name ?? `Vendedor #${log.seller.id}`
                        : log.seller_id
                        ? `Vendedor #${log.seller_id}`
                        : null
                    }
                  />
                  <Info
                    icon={Building2}
                    label="Unidade"
                    value={
                      log.unity?.name ??
                      (log.unity_id ? `Unidade #${log.unity_id}` : null)
                    }
                  />
                  <Info
                    icon={Globe}
                    label="Endereço IP"
                    value={log.ip_address}
                  />
                  <div className="sm:col-span-2">
                    <Info
                      icon={Route}
                      label="Rota"
                      value={
                        log.route
                          ? `${log.method ?? ""} ${log.route}`.trim()
                          : null
                      }
                    />
                  </div>
                </div>
              </Section>

              {log.user_agent && (
                <Section icon={Laptop} title="Dispositivo e navegador">
                  {(() => {
                    const agent = parseUserAgent(log.user_agent);

                    return (
                      <div className="space-y-3">
                        <div className="grid grid-cols-1 gap-2 sm:grid-cols-3">
                          <Info
                            icon={Globe2}
                            label="Navegador"
                            value={agent.browser}
                          />
                          <Info
                            icon={Monitor}
                            label="Sistema"
                            value={agent.os}
                          />
                          <Info
                            icon={Laptop}
                            label="Dispositivo"
                            value={agent.device}
                          />
                        </div>
                        <div className="rounded-lg border border-primary/20 bg-primary/5 p-3">
                          <p className="text-xs text-muted-foreground">
                            User agent completo
                          </p>
                          <p className="mt-1 break-all font-mono text-xs">
                            {log.user_agent}
                          </p>
                        </div>
                      </div>
                    );
                  })()}
                </Section>
              )}
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
};
